import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Popover from '@mui/material/Popover';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import InputBase from '@mui/material/InputBase';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  IconBell, IconCheck, IconChevronDown, IconChevronRight, IconGridDots, IconHelp, IconLogout, IconMenu2,
  IconSearch,
} from '@tabler/icons-react';
import {
  CATALOG, planStaffScope, staffScopeSearch, visibleApps, menuItemVisible, type AppEntry, type AppManifest, type NavGroup, type NavItem, type ScopeApp, type StaffScope,
} from '@infinix/registry';
import { AppDialog, AppIconTile, Avatar, NestMark, Skeleton, SoonBadge, tint, useMode, useToast, v } from '@infinix/ui';
import { layout } from '@infinix/tokens';
import { leaveTo, signOutTo } from './leave';
import { useSession, useScope, type Session } from './session';
import { OrgPicker } from './StaffScope';
import { useCrumbState } from './crumbs';
import { useBadgeState } from './badges';
import { NavIcon } from './icons';

/* ---------------------------------------------------------------------------------------- */

/** The Nest mark. Kept as `Logo` for existing callers. */
export function Logo({ size = 30 }: { size?: number }) {
  return <NestMark size={size} title="Nest" />;
}

function useCurrentApp(apps: AppManifest[]): AppManifest | undefined {
  const { pathname } = useLocation();
  return apps.find((a) => pathname === a.basePath || pathname.startsWith(`${a.basePath}/`));
}

/**
 * Whether a menu item shows. In a customer app that staff open at "All tenants" scope, only items that declare a
 * platform permission show; everywhere else the item's own (see) permission decides.
 */
export function useNavVisible() {
  const s = useSession();
  // The rule lives in @infinix/registry (access.ts, tested there).
  return useCallback((app: AppManifest, item: NavItem) => menuItemVisible(s, app, item), [s]);
}

/** The apps bundled in this deployable. Anything else is another app: reached by a full page load. */
const LocalAppsCtx = createContext<AppManifest[]>([]);

/**
 * Navigate anywhere in the suite: client-side inside this app, a normal page load into another app
 * (each app is its own deployable — docs/APP-CATALOG.md §3).
 */
export function useGoTo() {
  const local = useContext(LocalAppsCtx);
  const nav = useNavigate();
  return useCallback((path: string) => {
    const p = path.split(/[?#]/)[0] ?? path;
    const inLocal = local.some((a) => p === a.basePath || p.startsWith(`${a.basePath}/`));
    if (inLocal) nav(path);
    else leaveTo(path);
  }, [local, nav]);
}


/* ---- rail ------------------------------------------------------------------------------ */

/** Business-unit switcher, on the one Nest session: organization-level people switch; business-unit people are pinned. */
function ScopeSwitcher() {
  const s = useSession();
  const toast = useToast();
  const { org, label } = useScope();
  if (!org) return null;
  const pinned = s.level !== 'org';
  const options = [...(pinned ? [] : [{ id: null, name: 'All business units' }]), ...s.units.map((u) => ({ id: u.id, name: u.name }))];
  const initials = org.name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');
  return (
    <ScopeSwitcherView
      orgName={org.name} initials={initials} label={label} options={options} selected={s.buId} pinned={pinned}
      onPick={(id) => { if (id !== s.buId) s.setBuId(id).catch(() => toast("Couldn't switch business unit. Try again.")); }}
    />
  );
}

function ScopeSwitcherView({ orgName, initials, label, options, selected, pinned, onPick }: {
  orgName: string; initials: string; label: string; options: { id: string | null; name: string }[]; selected: string | null; pinned: boolean;
  onPick: (id: string | null) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Box
        component="button"
        type="button"
        aria-haspopup={pinned ? undefined : 'menu'}
        aria-label={`Business unit: ${label}`}
        disabled={pinned}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          all: 'unset', boxSizing: 'border-box', width: '100%', display: 'flex', alignItems: 'center', gap: '10px', p: '9px 10px',
          borderRadius: '11px', border: `1px solid ${v('border2')}`, background: v('panel'), cursor: pinned ? 'default' : 'pointer',
          '&:focus-visible': { outline: `2px solid ${v('accent')}` },
        }}
      >
        <Box sx={{ width: 30, height: 30, borderRadius: '8px', background: tint(v('accent'), 18), color: v('accent'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>{initials}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{orgName}</Box>
          <Box sx={{ fontSize: 12, color: v('caption'), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Box>
        </Box>
        {!pinned && <IconChevronDown size={14} color="var(--ix-caption)" />}
      </Box>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} slotProps={{ list: { dense: true, sx: { p: '5px', width: 240 } } }}>
        <Box sx={{ px: '10px', py: '6px', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', color: v('caption') }}>BUSINESS UNIT</Box>
        {options.map((o) => (
          <MenuItem key={o.id ?? 'all'} selected={selected === o.id} onClick={() => { onPick(o.id); setAnchor(null); }} sx={{ borderRadius: '7px', fontSize: 13, gap: '10px' }}>
            <Box sx={{ width: 14, display: 'flex' }}>{selected === o.id && <IconCheck size={14} stroke={2.6} color="var(--ix-accentText)" />}</Box>
            {o.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** Sign-out from any app: the account backend ends the one session; any failure keeps the person signed in and says so. */
function useSignOut() {
  const s = useSession();
  const toast = useToast();
  return () => {
    s.signOut()
      .then(() => signOutTo())
      .catch(() => toast("Couldn't sign you out. Check your connection and try again."));
  };
}

function RailNav({ groups, showBadges = true }: { groups: NavGroup[]; showBadges?: boolean }) {
  const badges = useBadgeState();
  const render = (g: NavGroup, gi: number) => (
    <Box key={gi} sx={{ mt: gi && !g.footer ? '14px' : 0 }}>
      {g.label && <Box sx={{ px: '10px', pb: '6px', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: v('caption') }}>{g.label}</Box>}
      {g.items.map((it) => {
        const count = showBadges && it.badgeKey ? badges[it.badgeKey] ?? 0 : 0;
        return (
          <Box
            key={it.path}
            component={NavLink}
            to={it.path}
            end={it.path.split('/').length <= 2}
            sx={{
              display: 'flex', alignItems: 'center', gap: '10px', height: 36, px: '10px', borderRadius: '9px', textDecoration: 'none',
              color: v('dim'), fontSize: 13.5, fontWeight: 500,
              '& svg': { color: v('caption') },
              '&:hover': { background: v('railHover'), color: v('text') },
              // Tinted rail: the active item lifts as a tab in the current app's accent.
              '&.active': { background: v('railActive'), boxShadow: v('railActiveShadow'), color: v('accentText'), fontWeight: 600, '& svg': { color: v('accent') } },
              '&:focus-visible': { outline: `2px solid ${v('accent')}` },
            }}
          >
            <NavIcon name={it.icon} />
            <Box sx={{ flex: 1 }}>{it.label}</Box>
            {count > 0 && (
              <Box className="ix-mono" sx={{ fontSize: 11, fontWeight: 600, px: '6px', borderRadius: '6px', color: v(toneVar(it.badgeTone)), background: tint(v(toneVar(it.badgeTone)), 14) }}>{count}</Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
  const main = groups.filter((g) => !g.footer);
  const foot = groups.filter((g) => g.footer);
  return (
    <Box component="nav" aria-label="Section" sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', px: '12px', py: '6px' }}>
      <Box sx={{ flex: 1 }}>{main.map(render)}</Box>
      {foot.length > 0 && <Box sx={{ mt: '14px', pt: '10px', borderTop: `1px solid ${v('railBorder')}` }}>{foot.map(render)}</Box>}
    </Box>
  );
}

const toneVar = (t?: string) => (t === 'danger' ? 'danger' : t === 'info' ? 'info' : t === 'neutral' ? 'dim' : 'warning');

/** The account menu, the same in every app: who you are, your preferences (My account) and sign-out. */
function AccountMenu() {
  const s = useSession();
  const goTo = useGoTo();
  const signOut = useSignOut();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Box
        component="button"
        type="button"
        aria-haspopup="menu"
        aria-label={`Account: ${s.email ?? ''}`}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          all: 'unset', boxSizing: 'border-box', flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '10px', p: '4px 6px', borderRadius: '10px', cursor: 'pointer',
          '&:hover': { background: v('railHover') }, '&:focus-visible': { outline: `2px solid ${v('accent')}` },
        }}
      >
        <Avatar initials={(s.email ?? '?').charAt(0).toUpperCase()} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.email}</Box>
          <Box sx={{ fontSize: 12, color: v('caption'), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.audience === 'staff' ? 'Nest staff' : (s.org?.name ?? '')}</Box>
        </Box>
      </Box>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'bottom', horizontal: 'left' }} slotProps={{ list: { dense: true, sx: { p: '5px', width: 250 } } }}>
        <Box sx={{ px: '10px', py: '8px' }}>
          <Box sx={{ fontSize: 13, fontWeight: 600 }}>{s.email}</Box>
          <Box sx={{ fontSize: 12, color: v('caption') }}>{s.audience === 'staff' ? 'Nest staff' : (s.org?.name ?? '')}</Box>
        </Box>
        <MenuItem onClick={() => { setAnchor(null); goTo('/account'); }} sx={{ borderRadius: '7px', fontSize: 13 }}>Personal preferences</MenuItem>
        <MenuItem onClick={() => { setAnchor(null); goTo('/account/security'); }} sx={{ borderRadius: '7px', fontSize: 13 }}>Sign-in and security</MenuItem>
        <MenuItem onClick={() => { setAnchor(null); signOut(); }} sx={{ borderRadius: '7px', fontSize: 13 }}>Sign out</MenuItem>
      </Menu>
    </>
  );
}

/**
 * Each app's colour carries its identity beyond the tile (owner, 2026-10-24): a soft wash of the app's accent on the
 * rail, and a stripe of it along the top bar. The page itself stays neutral, so tables, forms and status colours read
 * the same in every app. `--ix-accent` is the current app's accent (set below, per light/dark mode).
 */
const appRail = `color-mix(in srgb, ${v('accent')} 8%, ${v('rail')})`;
const appRailBorder = `color-mix(in srgb, ${v('accent')} 18%, ${v('railBorder')})`;

function Rail({ app }: { app: AppManifest | undefined }) {
  const s = useSession();
  const signOut = useSignOut();
  const visible = useNavVisible();
  const groups = useMemo(
    () => (app ? app.nav.map((g) => ({ ...g, items: g.items.filter((i) => visible(app, i)) })).filter((g) => g.items.length) : []),
    [app, visible],
  );
  return (
    <Box sx={{ width: layout.railWidth, flexShrink: 0, height: '100dvh', position: 'sticky', top: 0, display: 'flex', flexDirection: 'column', background: app ? appRail : v('rail'), borderRight: `1px solid ${app ? appRailBorder : v('railBorder')}` }}>
      <Box component={RouterLink} to={app?.basePath ?? '/'} reloadDocument={!app} sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '16px', height: layout.topBar, textDecoration: 'none', color: v('text') }}>
        {app ? <AppIcon app={app} size={32} /> : <Logo />}
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontSize: 15.5, fontWeight: 700, lineHeight: 1.15, whiteSpace: 'nowrap' }}>{app?.name ?? 'Nest'}</Box>
          <Box sx={{ fontSize: 11.5, color: v('caption'), fontWeight: 500 }}>Nest{app?.audience === 'staff' ? ' · staff' : ''}</Box>
        </Box>
      </Box>
      {/* Staff pick the organization and unit in the top bar (A-35); customers switch their own units here. */}
      {s.audience === 'staff' ? (
        s.staffView && app?.audience === 'customer' && !app.everyone && (
          <Box sx={{ px: '12px', pb: '12px' }}>
            {/* The organization's own actions are locked for staff (A-34); platform set-up follows the staff role. */}
            <Tooltip title="You see this organization as its admins do. Its own actions are locked for you; platform set-up follows your staff role.">
              <Box role="status" tabIndex={0} sx={{ px: '10px', py: '5px', borderRadius: '8px', fontSize: 12, fontWeight: 600, color: v('text'), background: tint(v('staff'), 12), boxShadow: `0 0 0 1px ${tint(v('staff'), 40)} inset` }}>
                Staff view
              </Box>
            </Tooltip>
          </Box>
        )
      ) : app?.everyone ? null : <Box sx={{ px: '12px', pb: '12px' }}><ScopeSwitcher /></Box>}
      {/* Badge counts are a tenant's work queues; they don't apply at All-tenants scope. */}
      <RailNav groups={groups} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', p: '10px 10px 10px 8px', borderTop: `1px solid ${v('railBorder')}` }}>
        <AccountMenu />
        <Tooltip title="Sign out">
          <Box component="button" type="button" aria-label="Sign out" onClick={signOut} sx={{ all: 'unset', cursor: 'pointer', display: 'flex', color: v('caption'), p: '6px', borderRadius: '8px', '&:hover': { background: v('railHover') }, '&:focus-visible': { outline: `2px solid ${v('accent')}` } }}>
            <IconLogout size={17} />
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}

/* ---- top bar --------------------------------------------------------------------------- */

/** App identity tile: the app's icon on its accent colour (launcher, rail header). */
export function AppIcon({ app, size = 48 }: { app: AppEntry; size?: number }) {
  return <AppIconTile glyph={app.icon} color={app.accent.light} size={size} />;
}

function AppTile({ app, onOpen }: { app: AppEntry; onOpen: () => void }) {
  const { mode } = useMode();
  const c = app.accent[mode];
  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      sx={{
        all: 'unset', boxSizing: 'border-box', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
        p: '12px 6px', borderRadius: '14px', textAlign: 'center', '&:hover': { background: v('sunk') }, '&:focus-visible': { outline: `2px solid ${c}` },
      }}
    >
      <AppIcon app={app} />
      <Box sx={{ fontSize: 12.5, fontWeight: 600, color: v('text') }}>{app.name}</Box>
    </Box>
  );
}

/** A launcher group; `query` is added to each app's address (staff in Ops open a tenant's app across all organizations). */
interface AppSection { label: string; apps: AppEntry[]; query?: string }

/**
 * The launcher's groups. Customers: their products, then Nest's apps. Staff (A-35): the organization in view's apps
 * (with none picked, only those that work across all organizations), then the platform (Ops) and My account.
 */
/**
 * An organization's apps in order (owner, 2026-10-18): its products first (Stay, SmartBill, Retail, Giving, each
 * opening on its own dashboard once in Nest), then Payments, then Organization. The grid and the top-bar tiles share it.
 */
const ORDER = ['payments', 'org'];
const rank = (a: AppEntry) => (a.product ? -1 : ORDER.includes(a.id) ? ORDER.indexOf(a.id) : ORDER.length);
const inOrder = (apps: AppEntry[]) => [...apps].sort((a, b) => rank(a) - rank(b));

function launcherSections(s: Session, apps: AppEntry[], current: AppManifest | undefined): AppSection[] {
  if (s.audience !== 'staff') {
    return [{ label: 'YOUR PRODUCTS', apps: apps.filter((a) => a.product) }, { label: 'NEST', apps: inOrder(apps.filter((a) => !a.product)) }];
  }
  // Ops is the platform: from there a tenant's app opens across all organizations (its address says so).
  const platform = current?.audience === 'staff';
  const org = platform ? null : s.org;
  const tenantApps = inOrder(apps.filter((a) => a.audience === 'customer' && !a.everyone && (org !== null || a.staffScope?.allTenants)));
  return [
    { label: org ? org.name.toUpperCase() : 'ACROSS ALL ORGANIZATIONS', apps: tenantApps, ...(platform ? { query: staffScopeSearch('', { org: null, unit: null }) } : {}) },
    { label: 'PLATFORM', apps: apps.filter((a) => a.audience === 'staff' || a.everyone) },
  ];
}

/** Google-style app grid: search and launch any app the user has. */
function Launcher({ sections }: { sections: AppSection[] }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [q, setQ] = useState('');
  const goTo = useGoTo();
  const match = (a: AppEntry) => `${a.name} ${a.description}`.toLowerCase().includes(q.toLowerCase());
  const shown = sections.map((sec) => ({ ...sec, apps: sec.apps.filter(match) })).filter((sec) => sec.apps.length);
  const open = (a: AppEntry, query = '') => { setAnchor(null); setQ(''); goTo(`${a.basePath}${query}`); };
  const grid = (sec: AppSection) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px', px: '8px' }}>
      {sec.apps.map((a) => <AppTile key={a.id} app={a} onOpen={() => open(a, sec.query)} />)}
    </Box>
  );
  return (
    <>
      <Tooltip title="Nest apps">
        {/* Used all the time (switching apps): larger and darker than the other top-bar icons (owner, 2026-10-18). */}
        <Box component="button" type="button" aria-label="Nest apps" aria-haspopup="dialog" onClick={(e) => setAnchor(e.currentTarget)} sx={{ ...iconBtn, width: 42, height: 42, color: v('text') }}>
          <IconGridDots size={27} stroke={2.75} />
        </Box>
      </Tooltip>
      <Popover
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => { setAnchor(null); setQ(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 340, mt: '6px', borderRadius: '16px', border: `1px solid ${v('border2')}`, background: v('popover') } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '14px 16px', borderBottom: `1px solid ${v('border')}` }}>
          <IconSearch size={17} color="var(--ix-caption)" />
          <InputBase autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search and launch" inputProps={{ 'aria-label': 'Search apps' }} sx={{ flex: 1, fontSize: 14.5 }} />
        </Box>
        <Box sx={{ py: '10px', maxHeight: 460, overflowY: 'auto' }}>
          {shown.map((sec, i) => (
            <Box key={sec.label}>
              <Box sx={{ px: '18px', pt: i ? '10px' : 0, pb: '4px', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', color: v('caption'), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sec.label}</Box>
              {grid(sec)}
            </Box>
          ))}
          {shown.length === 0 && <Box sx={{ p: '18px', fontSize: 13, color: v('dim') }}>No apps match.</Box>}
        </Box>
      </Popover>
    </>
  );
}

/**
 * One app as a tile in the top bar (owner, 2026-10-18: like the iPhone's icons): its icon, its name on hover, and for
 * the app in view a ring and an underline in its colour (owner, 2026-10-18: an underline, not a dot).
 */
function BarTile({ app, active, label, onOpen }: { app: AppEntry; active: boolean; label: string; onOpen: () => void }) {
  const { mode } = useMode();
  const c = app.accent[mode];
  return (
    <Tooltip title={label}>
      <Box
        component="button"
        type="button"
        aria-label={label}
        aria-current={active ? 'page' : undefined}
        onClick={onOpen}
        sx={{ all: 'unset', boxSizing: 'border-box', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', pt: '7px', borderRadius: '10px', '&:focus-visible': { outline: `2px solid ${c}`, outlineOffset: '2px' } }}
      >
        <Box sx={{ borderRadius: '10px', transition: 'transform 120ms', outline: active ? `2px solid ${c}` : 'none', outlineOffset: '2px', '&:hover': { transform: 'translateY(-1px)' } }}>
          <AppIcon app={app} size={34} />
        </Box>
        <Box aria-hidden sx={{ width: 20, height: 3, borderRadius: '2px', background: active ? c : 'transparent' }} />
      </Box>
    </Tooltip>
  );
}

/**
 * Quick access beside the grid: the apps of the organization in view, one click each (the grid keeps every app). Staff
 * see them once an organization is picked; customers always (their organization). My account isn't here: it's in the
 * account menu at the bottom left.
 */
function QuickApps({ sections, current }: { sections: AppSection[]; current: AppManifest | undefined }) {
  const s = useSession();
  const goTo = useGoTo();
  const staff = s.audience === 'staff';
  if (staff && (!s.org || current?.audience === 'staff')) return null;
  const apps = staff ? (sections[0]?.apps ?? []) : sections.flatMap((sec) => sec.apps).filter((a) => !a.everyone);
  if (!apps.length) return null;
  const scope = staff && s.org ? staffScopeSearch('', { org: s.org.id, unit: s.buId }) : '';
  return (
    <Box component="nav" aria-label="Apps" sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'flex-start', gap: '10px', ml: '2px' }}>
      {apps.map((a) => <BarTile key={a.id} app={a} active={a.id === current?.id} label={a.name} onOpen={() => goTo(`${a.basePath}${scope}`)} />)}
    </Box>
  );
}

const iconBtn = {
  all: 'unset', boxSizing: 'border-box', cursor: 'pointer', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: '9px', color: v('dim'), position: 'relative', '&:hover': { background: v('sunk'), color: v('text') }, '&:focus-visible': { outline: `2px solid ${v('accent')}` },
} as const;

function CommandPalette({ open, onClose, apps, current }: { open: boolean; onClose: () => void; apps: AppEntry[]; current: AppManifest | undefined }) {
  const goTo = useGoTo();
  const [q, setQ] = useState('');
  // This app's pages (its menu ships with it) plus every app the person can open.
  const visible = useNavVisible();
  const pages = current ? current.nav.flatMap((g) => g.items.filter((i) => visible(current, i)).map((i) => ({ app: current.name, label: i.label, path: i.path, icon: i.icon }))) : [];
  const appsList = apps.filter((a) => a.id !== current?.id).map((a) => ({ app: 'App', label: `Open ${a.name}`, path: a.basePath, icon: a.icon }));
  const items = [...pages, ...appsList];
  const shown = items.filter((i) => `${i.app} ${i.label}`.toLowerCase().includes(q.toLowerCase())).slice(0, 12);
  return (
    <Dialog open={open} onClose={onClose} slotProps={{ paper: { sx: { width: 600, maxWidth: 'calc(100% - 32px)', alignSelf: 'flex-start', mt: '90px' } } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '14px 16px', borderBottom: `1px solid ${v('border')}` }}>
        <IconSearch size={17} color="var(--ix-caption)" />
        <InputBase autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Jump to a page or an app…" inputProps={{ 'aria-label': 'Search pages' }} sx={{ flex: 1, fontSize: 14.5 }} />
        <Box className="ix-mono" sx={{ fontSize: 11, color: v('caption'), border: `1px solid ${v('border2')}`, borderRadius: '5px', px: '6px' }}>esc</Box>
      </Box>
      <Box sx={{ p: '6px', maxHeight: 420, overflowY: 'auto' }}>
        <Box sx={{ px: '10px', py: '8px', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', color: v('caption') }}>GO TO</Box>
        {shown.map((i) => (
          <Box
            key={i.path}
            component="button"
            type="button"
            onClick={() => { goTo(i.path); onClose(); setQ(''); }}
            sx={{ all: 'unset', boxSizing: 'border-box', width: '100%', display: 'flex', alignItems: 'center', gap: '12px', p: '9px 10px', borderRadius: '9px', cursor: 'pointer', '&:hover, &:focus-visible': { background: v('sunk') } }}
          >
            <NavIcon name={i.icon} size={16} color="var(--ix-caption)" />
            <Box sx={{ flex: 1, fontSize: 13.5 }}>{i.label}</Box>
            <Box sx={{ fontSize: 12, color: v('caption') }}>{i.app}</Box>
          </Box>
        ))}
        {shown.length === 0 && <Box sx={{ p: '16px', fontSize: 13, color: v('dim') }}>No pages match. Searching payments, people and records across apps is coming soon.</Box>}
      </Box>
    </Dialog>
  );
}

function Breadcrumbs() {
  const crumbs = useCrumbState();
  if (!crumbs.length) return null;
  return (
    <Box component="nav" aria-label="Breadcrumb" sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: '6px', fontSize: 13, minWidth: 0, mb: '10px', overflow: 'hidden', whiteSpace: 'nowrap' }}>
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        return (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            {i > 0 && <IconChevronRight size={13} color="var(--ix-faint)" />}
            {c.to && !last ? (
              <Box component={RouterLink} to={c.to} className={c.mono ? 'ix-mono' : undefined} sx={{ color: v('dim'), textDecoration: 'none', '&:hover': { color: v('text') } }}>{c.label}</Box>
            ) : (
              <Box component="span" aria-current={last ? 'page' : undefined} className={c.mono ? 'ix-mono' : undefined} sx={{ color: last ? v('text') : v('dim'), fontWeight: last ? 600 : 400 }}>{c.label}</Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function TopBar({ apps, current, onMenu, narrow }: { apps: AppEntry[]; current: AppManifest | undefined; onMenu: () => void; narrow: boolean }) {
  const s = useSession();
  const goTo = useGoTo();
  const [palette, setPalette] = useState(false);
  const sections = useMemo(() => launcherSections(s, apps, current), [s, apps, current]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPalette(true); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const staff = s.audience === 'staff';
  const ops = staff ? apps.find((a) => a.audience === 'staff' && !a.everyone) : undefined;
  return (
    <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: 10, height: layout.topBar, display: 'flex', alignItems: 'center', gap: '10px', px: { xs: '12px', md: '20px' }, background: tint(v('bg'), 92), backdropFilter: 'blur(8px)', borderBottom: `1px solid ${v('railBorder')}`, boxShadow: current ? `inset 0 3px 0 ${v('accent')}` : 'none' }}>
      {narrow && (
        <Box component="button" type="button" aria-label="Open navigation" onClick={onMenu} sx={iconBtn}><IconMenu2 size={19} /></Box>
      )}
      {/* Staff: the Ops dashboard, then the organization in view; the grid always sits right after (owner, 2026-10-18).
          "Staff view" in the rail says who's looking, so the bar carries no staff badge. */}
      {ops && <BarTile app={ops} active={current?.audience === 'staff'} label="Ops dashboard" onOpen={() => goTo(ops.basePath)} />}
      {staff && <OrgPicker current={current} goTo={goTo} />}
      <Launcher sections={sections} />
      <QuickApps sections={sections} current={current} />
      <Box sx={{ flex: 1 }} />
      <Box
        component="button"
        type="button"
        onClick={() => setPalette(true)}
        aria-label="Search"
        sx={{ all: 'unset', boxSizing: 'border-box', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', height: 36, width: { xs: 36, lg: 300 }, px: { xs: 0, lg: '12px' }, justifyContent: { xs: 'center', lg: 'flex-start' }, borderRadius: '9px', border: `1px solid ${v('border2')}`, background: v('sunk'), color: v('caption'), fontSize: 13, '&:focus-visible': { outline: `2px solid ${v('accent')}` } }}
      >
        <IconSearch size={15} />
        <Box component="span" sx={{ display: { xs: 'none', lg: 'inline' }, flex: 1 }}>Search pages and records…</Box>
        <Box component="span" className="ix-mono" sx={{ display: { xs: 'none', lg: 'inline' }, fontSize: 11, border: `1px solid ${v('border2')}`, borderRadius: '5px', px: '5px' }}>⌘K</Box>
      </Box>
      <Tooltip title="Notifications — coming soon (needs Core notification feed C-17)">
        <Box component="button" type="button" aria-label="Notifications, coming soon" sx={iconBtn}><IconBell size={18} /></Box>
      </Tooltip>
      <Tooltip title="Help — coming soon (support channel decision D-05)">
        <Box component="button" type="button" aria-label="Help, coming soon" sx={iconBtn}><IconHelp size={18} /></Box>
      </Tooltip>
      <CommandPalette open={palette} onClose={() => setPalette(false)} apps={sections.flatMap((sec) => sec.apps)} current={current} />
    </Box>
  );
}

/* ---- the organization in the address (staff, A-35) -------------------------------------- */

/**
 * Keeps the address and the session in step for Nest staff (rules in @infinix/registry staffScope.ts): a tenant's app
 * carries `?org=` (and `&unit=`); an address naming another organization switches the session to it (back button,
 * bookmarks, shared links); Ops lets go of the organization. Returns false while the session switches, so a page never
 * shows one organization's data under another's address.
 */
function useStaffScopeInAddress(current: AppManifest | undefined): boolean {
  const s = useSession();
  const toast = useToast();
  const nav = useNavigate();
  const { pathname, search, hash, key } = useLocation();
  const busy = useRef(false);
  // The history entry already in step with the session. The session is shared by every tab: when another tab changes
  // it, this entry follows it (its address is rewritten) instead of switching it back, so two tabs never fight.
  // Switching (or, in Ops, letting go) happens only for an entry this tab navigated to.
  const settled = useRef<string | null>(null);
  // A scope the session refused for this history entry: the address then takes the session's (no retry loop).
  const [refused, setRefused] = useState<{ key: string; to: StaffScope } | null>(null);
  const app: ScopeApp = s.status !== 'signed-in' || s.audience !== 'staff' || !current || current.everyone ? 'none' : current.audience === 'staff' ? 'platform' : 'tenant';
  const session: StaffScope = { org: s.org?.id ?? null, unit: s.buId };
  let plan = planStaffScope(app, search, session, refused?.key === key ? refused.to : null);
  if (settled.current === key) {
    if (plan.kind === 'switch') plan = planStaffScope(app, search, session, plan.to);
    else if (plan.kind === 'clear') plan = { kind: 'none' };
  }
  const planKey = JSON.stringify(plan);

  useEffect(() => {
    if (plan.kind === 'none') {
      settled.current = key;
      return;
    }
    if (plan.kind === 'write') {
      // A page can redirect in the same commit (Settings → its first tab): its effect runs before this one, so the
      // address may already have moved on. Writing the old path back would undo that redirect and leave the page
      // empty; the new address is planned on its own render instead.
      if (window.location.pathname !== pathname) return;
      nav({ pathname, search: plan.search, hash }, { replace: true });
      return;
    }
    if (busy.current || (plan.kind !== 'switch' && plan.kind !== 'clear')) return;
    busy.current = true;
    const to = plan.kind === 'switch' ? plan.to : { org: null, unit: null };
    const run = async () => {
      if (to.org !== session.org) await s.setTenant(to.org);
      if (to.unit) await s.setBuId(to.unit);
    };
    run()
      .catch(() => {
        toast(to.org ? "Couldn't open that organization. You're still looking at the one you had open." : "Couldn't leave the organization in view. Try again.");
        setRefused({ key, to });
      })
      .finally(() => { busy.current = false; });
    // Runs when the plan changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey, key]);

  return plan.kind !== 'switch';
}

/* ---- the shell ------------------------------------------------------------------------- */

/**
 * The suite chrome. `local` = the apps bundled in this deployable (their menus); the launcher lists every
 * app in the catalog the person may open.
 */
export function Shell({ local }: { local: AppManifest[] }) {
  const s = useSession();
  const narrow = useMediaQuery('(max-width:900px)');
  const [drawer, setDrawer] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setDrawer(false); window.scrollTo(0, 0); }, [pathname]);

  const apps = useMemo(() => {
    return visibleApps(s.identity, CATALOG, s.view?.staffTenantAccess);
  }, [s.identity, s.view?.staffTenantAccess]);
  const current = useCurrentApp(local);
  const scopeReady = useStaffScopeInAddress(current);
  const { mode } = useMode();
  const accent = current?.accent[mode];
  useEffect(() => {
    if (!current || !s.email) return;
    try { window.localStorage.setItem(lastAppKey(s.email), current.id); } catch { /* convenience only */ }
  }, [current, s.email]);
  useEffect(() => {
    // The current app's accent re-themes accent, buttons and focus everywhere (including portals). Status colours stay fixed.
    const st = document.documentElement.style;
    const keys = ['--ix-accent', '--ix-accentText', '--ix-button', '--ix-buttonRing'];
    if (accent) keys.forEach((k) => st.setProperty(k, accent)); else keys.forEach((k) => st.removeProperty(k));
  }, [accent]);

  const rail = <Rail app={current} />;
  return (
    <LocalAppsCtx.Provider value={local}>
    <Box sx={{ display: 'flex', minHeight: '100dvh', background: v('bg') }}>
      <Box component="a" href="#main" sx={{ position: 'absolute', left: -9999, '&:focus': { left: 12, top: 12, zIndex: 100, background: v('panel'), p: 1, borderRadius: 1 } }}>Skip to content</Box>
      {narrow ? (
        <Drawer open={drawer} onClose={() => setDrawer(false)} slotProps={{ paper: { sx: { background: current ? appRail : v('rail') } } }}>{rail}</Drawer>
      ) : rail}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TopBar apps={apps} current={current} onMenu={() => setDrawer(true)} narrow={narrow} />
        <Box component="main" id="main" sx={{ flex: 1, p: { xs: '18px 14px 40px', md: `${layout.contentPadY}px ${layout.contentPadX}px 48px` }, minWidth: 0 }}>
          {/* Where you are, above the page title (the top bar holds the app tiles). */}
          {scopeReady && <Breadcrumbs />}
          {scopeReady ? <Outlet /> : <Box role="status" aria-label="Opening the organization"><Skeleton height={48} /><Box sx={{ mt: 2 }}><Skeleton height={300} /></Box></Box>}
        </Box>
        <IdleWarning />
      </Box>
    </Box>
    </LocalAppsCtx.Provider>
  );
}

/** Shown when a page needs a permission the user lacks (direct URL access). The BFF enforces the same rule. */
export function NoAccess({ what }: { what: string }) {
  return (
    <Box sx={{ maxWidth: 520, mx: 'auto', mt: 8, textAlign: 'center' }}>
      <Box component="h1" sx={{ fontSize: 22, fontWeight: 700 }}>You don't have access to {what}</Box>
      <Box sx={{ mt: 1, color: v('dim'), fontSize: 14 }}>Ask your organization's admin for a role that includes it.</Box>
    </Box>
  );
}

export function Soon({ reason, children }: { reason: string; children?: ReactNode }) {
  return <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>{children}<SoonBadge reason={reason} /></Box>;
}

/** localStorage key for the last app a person used (A-11), keyed by a hash of their email, never the email itself. */
export function lastAppKey(email: string): string {
  let h = 5381;
  for (const ch of email.toLowerCase()) h = ((h << 5) + h + ch.charCodeAt(0)) >>> 0;
  return `nest.lastApp.${h.toString(36)}`;
}

/** Before the idle sign-out (30 minutes without input in any tab), offer to stay signed in. */
function IdleWarning() {
  const s = useSession();
  const [busy, setBusy] = useState(false);
  if (!s.idleWarning) return null;
  const left = s.idleWarning.secondsLeft;
  const stay = () => {
    setBusy(true);
    s.staySignedIn().catch(() => undefined).finally(() => setBusy(false));
  };
  return (
    <AppDialog open title="Still there?" onClose={stay} confirmLabel="Stay signed in" onConfirm={stay} confirmDisabled={busy} cancelLabel="Dismiss">
      <Box sx={{ fontSize: 14 }}>
        For your security, Nest signs you out after 30 minutes without activity. Signing out in{' '}
        <b>{Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')}</b>.
      </Box>
    </AppDialog>
  );
}
